import React, { useEffect, useMemo, useState } from "react";

import Carousel from "react-multi-carousel";

import {
  Box,
  Button,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";

import DownloadIcon from "@mui/icons-material/Download";

import { banner } from "../../../Model/home";

import {
  actionByID,
  acutionOngoing,
} from "../../../Model/Auction";

import "react-multi-carousel/lib/styles.css";

/* ------------------------------------------------------------------ */
/* Config                                                              */
/* ------------------------------------------------------------------ */

const RESPONSIVE = {
  all: {
    breakpoint: {
      max: 10000,
      min: 0,
    },
    items: 1,
    slidesToSlide: 1,
  },
};

/*
 * Banner sizes:
 *
 * Desktop: 1920 x 800
 * Mobile:   751 x 980
 *
 * We use aspectRatio instead of a fixed height.
 * This prevents the banner from being cropped.
 */
const BANNER_FRAME = {
  desktopRatio: "1920 / 800",
  mobileRatio: "751 / 980",
  background: "#fcfcfc",
};

/*
 * Banner media.
 *
 * contain = never crop the original image.
 */
const mediaStyle = {
  display: "block",
  width: "100%",
  height: "100%",
  objectFit: "contain",
  objectPosition: "center",
  backgroundColor: BANNER_FRAME.background,
};

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function getFrameSx(isDesktop) {
  return {
    position: "relative",
    width: "100%",

    /*
     * Desktop:
     * 1920 / 800 = 2.4
     *
     * Mobile:
     * 751 / 980
     */
    aspectRatio: isDesktop
      ? BANNER_FRAME.desktopRatio
      : BANNER_FRAME.mobileRatio,

    overflow: "hidden",

    bgcolor: BANNER_FRAME.background,
  };
}

/**
 * banner_link values the CMS uses to mean
 * "this banner is not clickable".
 */
const NO_LINK_VALUES = new Set([
  "",
  "nodata",
  "nenasaguil",
]);

function hasLink(link) {
  return Boolean(link) && !NO_LINK_VALUES.has(link);
}

function isVideoBanner(row) {
  return Number(row.isImage) === 0;
}

/**
 * Turns an actionByID() response into
 * { view, download } or null.
 */
function toCatalog(response) {
  const item = Array.isArray(response)
    ? response[0]
    : null;

  if (
    !item?.catalog_url ||
    !item?.catalog_download
  ) {
    return null;
  }

  return {
    view: item.catalog_url,
    download: `/pdf/${item.catalog_download}.pdf`,
  };
}

/**
 * Resolves when the image has loaded OR failed.
 */
function preloadImage(src) {
  return new Promise((resolve) => {
    const img = new Image();

    img.onload = resolve;
    img.onerror = resolve;

    img.src = src;
  });
}

/* ------------------------------------------------------------------ */
/* Catalog Buttons                                                     */
/* ------------------------------------------------------------------ */

const catalogButtonSx = {
  minHeight: 44,

  px: {
    xs: 2,
    sm: 3.5,
  },

  fontSize: {
    xs: 14,
    sm: 16,
  },

  fontWeight: 600,

  letterSpacing: "0.04em",

  textTransform: "none",

  color: "#fff",

  bgcolor: "rgba(17,17,17,0.88)",

  borderRadius: 0,

  boxShadow: "none",

  whiteSpace: "nowrap",

  "&:hover": {
    bgcolor: "#000",
    boxShadow: "none",
  },

  "&:focus-visible": {
    outline: "2px solid #fff",
    outlineOffset: "2px",
  },
};

function CatalogButtons({ catalog }) {
  if (!catalog) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "absolute",

        left: 0,
        right: 0,

        bottom: {
          xs: 16,
          sm: 35,
        },

        zIndex: 2,

        display: "flex",

        justifyContent: "center",

        gap: {
          xs: 1.5,
          sm: 3,
        },

        px: 2,
      }}
    >
      <Button
        variant="contained"
        href={catalog.view}
        target="_blank"
        rel="noopener noreferrer"
        sx={catalogButtonSx}
      >
        E-Catalog
      </Button>

      <Button
        variant="contained"
        href={catalog.download}
        download
        startIcon={<DownloadIcon />}
        sx={catalogButtonSx}
      >
        Download PDF
      </Button>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* Banner Slide                                                        */
/* ------------------------------------------------------------------ */

function BannerSlide({
  row,
  catalog,
  isDesktop,
  isFirst,
}) {
  const isVideo = isVideoBanner(row);

  /*
   * show_button = 1
   * → Show catalog buttons.
   * → Banner is NOT clickable.
   *
   * show_button = 0
   * → Hide catalog buttons.
   * → Whole banner is clickable using banner_link.
   */
  const isButton = Number(row.show_button) === 1;

  const clickable =
    !isButton && hasLink(row.banner_link);

  const showCatalog =
    isButton &&
    !isVideo &&
    Boolean(catalog);

  const media = isVideo ? (
    <video
      autoPlay
      loop
      muted
      playsInline
      style={mediaStyle}
    >
      <source
        src={row.banner_url}
        type="video/mp4"
      />
    </video>
  ) : (
    <img
      src={row.banner_url}
      alt="León Gallery featured banner"
      fetchPriority={
        isFirst ? "high" : "auto"
      }
      draggable={false}
      style={mediaStyle}
    />
  );

  return (
    <Box sx={getFrameSx(isDesktop)}>
      {clickable ? (
        <Box
          component="a"
          href={row.banner_link}
          sx={{
            display: "block",
            width: "100%",
            height: "100%",
            cursor: "pointer",
          }}
        >
          {media}
        </Box>
      ) : (
        media
      )}

      {showCatalog && (
        <CatalogButtons
          catalog={catalog}
        />
      )}
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* Banner Home                                                         */
/* ------------------------------------------------------------------ */

function BannerHome() {
  /*
   * Desktop = 700px and above
   * Mobile  = below 700px
   */
  const isDesktop = useMediaQuery(
    "(min-width:700px)",
    {
      noSsr: true,
    }
  );

  const [banners, setBanners] =
    useState([]);

  const [bannersLoaded, setBannersLoaded] =
    useState(false);

  const [imagesReady, setImagesReady] =
    useState(false);

  const [catalogs, setCatalogs] =
    useState({
      current: null,
      upcoming: null,
    });

  /* -------------------------------------------------------------- */
  /* Fetch banners and catalog links once                           */
  /* -------------------------------------------------------------- */

  useEffect(() => {
    let isActive = true;

    (async () => {
      try {
        const results = await banner();

        if (
          isActive &&
          Array.isArray(results)
        ) {
          setBanners(results);
        }
      } catch (error) {
        console.error(
          "Failed to load banners:",
          error
        );
      } finally {
        if (isActive) {
          setBannersLoaded(true);
        }
      }
    })();

    /* ------------------------------------------------------------ */
    /* Fetch auction catalogs                                       */
    /* ------------------------------------------------------------ */

    (async () => {
      try {
        const ongoing =
          await acutionOngoing();

        if (
          !Array.isArray(ongoing) ||
          ongoing.length === 0
        ) {
          return;
        }

        const [current, upcoming] =
          await Promise.all(
            ongoing
              .slice(0, 2)
              .map((item) =>
                actionByID(item.id)
              )
          );

        if (isActive) {
          setCatalogs({
            current: toCatalog(current),
            upcoming: toCatalog(upcoming),
          });
        }
      } catch (error) {
        console.error(
          "Failed to load auction catalogs:",
          error
        );
      }
    })();

    return () => {
      isActive = false;
    };
  }, []);

  /* -------------------------------------------------------------- */
  /* Desktop and mobile have separate banner sets                   */
  /* -------------------------------------------------------------- */

  const slides = useMemo(
    () =>
      banners.filter(
        (row) =>
          Number(row.isMobile) ===
          (isDesktop ? 0 : 1)
      ),
    [banners, isDesktop]
  );

  /* -------------------------------------------------------------- */
  /* Preload images                                                 */
  /* -------------------------------------------------------------- */

  useEffect(() => {
    if (slides.length === 0) {
      return undefined;
    }

    let isActive = true;

    setImagesReady(false);

    const images = slides.filter(
      (row) => !isVideoBanner(row)
    );

    Promise.all(
      images.map((row) =>
        preloadImage(row.banner_url)
      )
    ).then(() => {
      if (isActive) {
        setImagesReady(true);
      }
    });

    return () => {
      isActive = false;
    };
  }, [slides]);

  /* -------------------------------------------------------------- */
  /* Nothing to show                                                */
  /* -------------------------------------------------------------- */

  if (
    bannersLoaded &&
    slides.length === 0
  ) {
    return null;
  }

  const isLoading =
    !bannersLoaded || !imagesReady;

  /* -------------------------------------------------------------- */
  /* Render                                                          */
  /* -------------------------------------------------------------- */

  return (
    <section
      className="main-slider style1"
      style={{
        background:
          BANNER_FRAME.background,
      }}
    >
      <div
        className="rev_slider_wrapper fullwidthbanner-container"
        data-source="gallery"
      >
        {isLoading ? (
          /*
           * Same aspect ratio as the banner,
           * so the page doesn't jump while loading.
           */
          <Box
            sx={{
              ...getFrameSx(isDesktop),

              display: "flex",

              alignItems: "center",

              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Carousel
            responsive={RESPONSIVE}
            className="container2"
            itemClass="carousel-itemAction"
            infinite={false}
          >
            {slides.map((row, index) => (
              <BannerSlide
                key={
                  row.id ??
                  row.banner_url ??
                  index
                }
                row={row}
                isDesktop={isDesktop}
                isFirst={index === 0}
                catalog={
                  index === 0
                    ? catalogs.current
                    : catalogs.upcoming
                }
              />
            ))}
          </Carousel>
        )}
      </div>
    </section>
  );
}

export default BannerHome;